import React, { useState } from 'react';
import GenericDialog from "../../common/GenericDialog";
import { DatePicker, Divider, Spin } from "antd";
import dayjs from "dayjs";
import Button from "../../common/Button";
import {AutoAwesomeRounded, ContentCopyRounded} from "@mui/icons-material";
import TypingEffect from "./TypingEffect";

const { RangePicker } = DatePicker;

const GenerateAIStudentReportDialog = ({ open, handleDialogOpen, student }) => {
    const [selectedRange, setSelectedRange] = useState([]);
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState('');

    const presets = [
        {
            label: 'Last Week',
            value: [dayjs().subtract(7, 'day'), dayjs()],
        },
        {
            label: 'Last Month',
            value: [dayjs().subtract(1, 'month'), dayjs()],
        },
        {
            label: 'Last 3 Months',
            value: [dayjs().subtract(3, 'month'), dayjs()],
        },
        {
            label: 'Last 10 Weeks',
            value: [dayjs().subtract(10, 'week'), dayjs()],
        },
    ];

    const handleDateChange = (dates) => {
        setSelectedRange(dates);
    };

    const handleGenerate = async () => {
        setLoading(true);
        setSummary('');
        await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate delay

        const diffInWeeks = selectedRange?.[0] && selectedRange?.[1]
            ? selectedRange[1].diff(selectedRange[0], 'week') || 1
            : 'a few';

        const summaries = [
            `Over the last ${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''}, ${extractFirstName(student?.name)} has completed 48 questions, 14 activities, and 12 scorchers. Their results are on an upward positive trend and show that they have been working hard.`,
            `Over the last ${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''}, ${extractFirstName(student?.name)} has made consistent efforts, completing 28 questions and several activities. There's clear evidence they’ve been trying hard.`,
            `Over the last ${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''}, ${extractFirstName(student?.name)} has completed only a few activities and their results are trending downward. More focus and effort are needed moving forward.`
        ];

        // Randomly pick a summary
        const randomIndex = Math.floor(Math.random() * summaries.length);
        setSummary(summaries[randomIndex]);
        setLoading(false);
    };

    const extractFirstName = (fullName) => {
        if (typeof fullName === 'string' && fullName.includes(',')) {
            return fullName.split(', ')[1]?.trim() || '';
        }
        return '';
    };

    return (
        <GenericDialog
            title={`Generate summary`}
            open={open}
            fullWidth={true}
            maxWidth={'xs'}
            onClose={() => {
                setSummary('');
                handleDialogOpen(false);
            }}
            buttons={[
                {
                    name: 'Close',
                    extraProps: { type: 'text' },
                },
            ]}
        >
            <div style={{ maxWidth: 600, padding: '0.5rem 1rem 0 1rem' }}>
                <p>Use AI to generate a summary of {extractFirstName(student?.name)}'s work. Set the range then select <strong>Generate</strong> to see and copy the summary.</p>
                <Divider />
                <RangePicker
                    presets={presets}
                    value={selectedRange}
                    onChange={handleDateChange}
                    style={{ width: '100%' }}
                />

                {summary && (
                    <>
                        <div className={'ch-ai-card'}>
                            <div className={'ch-ai-card-content'}>
                                <p style={{fontWeight: 700, color: '#7D7D7D', fontSize: '12px'}}><AutoAwesomeRounded style={{ marginRight: '0.25rem' }} fontSize={'18px'} /> AI Summary</p>
                                <TypingEffect text={summary} />
                            </div>
                        </div>
                    </>
                )}

                <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={handleGenerate} disabled={loading || selectedRange.length === 0}>
                        {loading ? <Spin size="small" /> : <AutoAwesomeRounded style={{ marginRight: '0.5rem' }} />}
                        {loading ? 'Generating...' : !!summary ? 'Regenerate' : 'Generate'}
                    </Button>
                    {(!!summary && !loading) &&
                        <Button type={'outline'}>
                            <ContentCopyRounded style={{ marginRight: '0.5rem' }}/> Copy
                        </Button>
                    }
                </div>
            <Divider style={{marginBottom: '0', width: '100%'}}  />
            </div>
        </GenericDialog>
    );
};

export default GenerateAIStudentReportDialog;
